import { useState } from "react"
import { VideoPlayer } from "@/components/common/VideoPlayer"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Check, Copy, ExternalLink } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"

interface ShareSuccessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shareId: string
  videoUrl: string
  thumbnailUrl: string | null
  ratio?: string
}

function getPreviewStyle(ratio: string) {
  const [rawW, rawH] = ratio.split(":").map(Number)
  const w = Number.isFinite(rawW) && rawW > 0 ? rawW : 16
  const h = Number.isFinite(rawH) && rawH > 0 ? rawH : 9
  const maxWidthByHeight = Math.floor((480 * w) / h)

  return {
    aspectRatio: `${w}/${h}`,
    width: `min(100%, ${maxWidthByHeight}px)`,
    maxHeight: "480px",
  }
}

export function ShareSuccessDialog({
  open,
  onOpenChange,
  shareId,
  videoUrl,
  thumbnailUrl,
  ratio = "16:9",
}: ShareSuccessDialogProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const previewStyle = getPreviewStyle(ratio)

  // 공유 URL 생성 (VITE_BASE_PATH의 trailing slash 제거)
  const basePath = (import.meta.env.VITE_BASE_PATH || '').replace(/\/$/, '')
  const shareUrl = `${window.location.origin}${basePath}/share/${shareId}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      toast.success(t('share.urlCopied'))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t('share.copyFailed'))
    }
  }

  const handleOpenInNewTab = () => {
    window.open(shareUrl, '_blank')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-600">
            <Check className="w-5 h-5" />
            {t('share.successTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('share.successDescription')}
            <br />
            {t('share.copyUrlHint')}
          </DialogDescription>
        </DialogHeader>

        {/* 썸네일 + 재생 */}
        <div className="flex justify-center py-4">
          <VideoPlayer
            src={videoUrl}
            poster={thumbnailUrl ?? undefined}
            controls
            muted
            preload="none"
            className="max-w-full mx-auto rounded-lg border border-slate-200 overflow-hidden bg-slate-100"
            style={previewStyle}
          />
        </div>

        {/* URL 복사 영역 */}
        <div className="flex items-center gap-2">
          <Input
            value={shareUrl}
            readOnly
            className="flex-1 text-sm bg-slate-50"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleCopy}
            className="shrink-0"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleOpenInNewTab}
            className="shrink-0"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            {t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
